import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BadgeCheck, MessageCircle } from "lucide-react";
import Layout from "@/components/Layout";
import api from "@/lib/api";

const NICHES = ["all", "affiliate", "dropshipping", "freelance", "crypto", "content", "health", "ai"];

export default function Mentors() {
  const [mentors, setMentors] = useState([]);
  const [niche, setNiche] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = niche === "all" ? "" : `?niche=${niche}`;
    api.get(`/mentors${q}`).then((r) => setMentors(r.data)).finally(() => setLoading(false));
  }, [niche]);

  return (
    <Layout>
      <div className="max-w-5xl mx-auto py-8 px-4 md:px-8">
        <div className="flex items-center gap-3 mb-2">
          <BadgeCheck size={22} className="text-[#00E676]" strokeWidth={2} />
          <h1 className="font-heading font-bold text-4xl tracking-tight">Mentors</h1>
        </div>
        <p className="text-sm text-white/45 font-mono mb-6">Verified operators who've actually done it. DM them.</p>

        <div className="flex items-center gap-1.5 mb-6 overflow-x-auto pb-1" data-testid="mentor-filters">
          {NICHES.map((n) => (
            <button
              key={n}
              data-testid={`mentor-niche-${n}`}
              onClick={() => setNiche(n)}
              className={`shrink-0 text-[10px] font-mono uppercase tracking-wider px-3 py-1.5 rounded-md border transition ${
                niche === n
                  ? "border-[#00E676] text-[#00E676] bg-[#00E676]/8"
                  : "border-white/8 text-white/45 hover:text-white"
              }`}
            >
              {n}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{[1, 2, 3].map((i) => <div key={i} className="surface rounded-xl h-48 shimmer" />)}</div>
        ) : mentors.length === 0 ? (
          <div className="surface rounded-xl p-12 text-center">
            <BadgeCheck size={32} className="text-[#00E676] mx-auto mb-3" strokeWidth={1.5} />
            <div className="font-heading font-bold text-xl mb-1">No mentors here yet.</div>
            <div className="text-sm text-white/50">Mark yourself as a mentor from your profile.</div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {mentors.map((m, i) => (
              <motion.div
                key={m.user_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="surface rounded-xl p-5 card-hover"
                data-testid={`mentor-${m.user_id}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <Link to={`/profile/${m.user_id}`}>
                    <img
                      src={m.picture || `https://api.dicebear.com/9.x/notionists/svg?seed=${m.user_id}`}
                      alt=""
                      className="w-12 h-12 rounded-md object-cover bg-white/5"
                    />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/profile/${m.user_id}`} className="font-semibold hover:text-[#00E676] transition flex items-center gap-1.5">
                      {m.name} <BadgeCheck size={13} className="text-[#00E676]" fill="#00E676" stroke="#0A0A0A" strokeWidth={2.5} />
                    </Link>
                    <div className="text-[11px] font-mono text-[#00E676]">
                      ${Math.round(m.total_earnings || 0).toLocaleString()} earned
                    </div>
                  </div>
                </div>
                <p className="text-sm text-white/65 leading-relaxed mb-3 line-clamp-2">{m.bio}</p>
                {m.mentor_specialty && (
                  <div className="text-[11px] font-mono text-white/45 mb-3">
                    <span className="uppercase tracking-wider text-white/35">Specialty:</span> {m.mentor_specialty}
                  </div>
                )}
                <div className="flex flex-wrap gap-1 mb-4">
                  {(m.niches || []).map((n) => (
                    <span key={n} className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded bg-white/5 text-white/60">{n}</span>
                  ))}
                </div>
                <Link
                  to={`/messages?to=${m.user_id}`}
                  data-testid={`mentor-dm-${m.user_id}`}
                  className="surface-elev rounded-md py-2 text-xs font-semibold flex items-center justify-center gap-1.5 hover:bg-white/[0.08] transition"
                >
                  <MessageCircle size={13} /> Send DM
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
